import { useGetList, useRecordContext } from "react-admin";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { PageTitle } from "../../misc/PageTitle";
import { Button, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Row } from "../../misc/Row";
import { type DrivingSchoolAutoForm } from "../../providers/autoFormsProvider";
import { grants } from "../../backoffice.access_control";
import { AccessDenied } from "../../misc/AccessDenied";
import { Column } from "../../misc/Column";
import { useMemo } from "react";
import { EmptyState } from "../../misc/EmptyState";
import { AddAutoFormDialog } from "./AddAutoFormDialog";
import { useDialog } from "../../hooks/useDialog";
import { ResponsiveListSurface } from "../../misc/ResponsiveListSurface";
import { AutoFormsDataGrid } from "./AutoFormsDataGrid";

export function DrivingSchoolAutoForms() {
  const drivingSchool = useRecordContext<DrivingSchool>();
  const { dialogProps: createDialogProps, openDialog: openCreateDialog } = useDialog();
  const { data: drivingSchoolAutoForms = [] } = useGetList<DrivingSchoolAutoForm>(
    "autoForms",
    {
      filter: {
        drivingSchoolId: drivingSchool?.id,
      },
      sort: { field: "name", order: "ASC" },
      meta: { includeDeleted: true },
    },
    { enabled: !!drivingSchool?.id && grants.has("DrivingSchool:AutoForm:list") },
  );

  const { activeDrivingSchoolAutoForms, deletedDrivingSchoolAutoForms } = useMemo(
    () =>
      drivingSchoolAutoForms.reduce<{
        activeDrivingSchoolAutoForms: DrivingSchoolAutoForm[];
        deletedDrivingSchoolAutoForms: DrivingSchoolAutoForm[];
      }>(
        (acc, it) => {
          if (it.deleted) {
            acc.deletedDrivingSchoolAutoForms.push(it);
          } else {
            acc.activeDrivingSchoolAutoForms.push(it);
          }
          return acc;
        },
        {
          activeDrivingSchoolAutoForms: [],
          deletedDrivingSchoolAutoForms: [],
        },
      ),
    [drivingSchoolAutoForms],
  );

  if (!grants.has("DrivingSchool:AutoForm:list")) {
    return <AccessDenied />;
  }

  if (!drivingSchool) {
    return (
      <>
        <PageTitle>Auto-Formulare</PageTitle>
        <LoadingIndicator />
      </>
    );
  }

  const empty = <EmptyState />;

  return (
    <Column>
      <Alert severity="info" sx={{ mb: 2 }}>
        Mit Auto-Formularen erstellst du PDFs (z.&nbsp;B. Verträge oder AGB) direkt aus dem Schülerprofil oder lädst sie
        für ausgewählte Teilnehmer eines BKF-Kurses herunter. Einmal eingerichtet, werden die Schülerdaten automatisch
        an der richtigen Stelle eingefügt.
      </Alert>
      <div style={{ position: "relative" }}>
        <Row
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: "30px", minHeight: "32px", columnGap: 2, flexWrap: { xs: "wrap", sm: "nowrap" }, rowGap: 1 }}
        >
          <PageTitle documentTitle="Auto-Formulare">
            <h2 style={{ margin: 0, lineHeight: 0.83 }}>Auto-Formulare</h2>
          </PageTitle>
          {grants.has("DrivingSchool:AutoForm:create") && (
            <>
              <IconButton
                aria-label="Auto-Formular hinzufügen"
                color="primary"
                onClick={openCreateDialog}
                size="small"
                sx={{ display: { xs: "inline-flex", sm: "none" } }}
              >
                <AddIcon />
              </IconButton>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
                size="small"
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
              >
                Hinzufügen
              </Button>
            </>
          )}
        </Row>
        <ResponsiveListSurface variant="compactStandalone" data-testid="DrivingSchoolAutoForms">
          <AutoFormsDataGrid
            autoForms={activeDrivingSchoolAutoForms}
            empty={empty}
            isEditable={grants.has("DrivingSchool:AutoForm:edit")}
            showRegistrationStatus
          />
        </ResponsiveListSurface>
      </div>
      {deletedDrivingSchoolAutoForms.length > 0 && (
        <Column mt={8}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 0.83 }}>
            Gelöschte Auto-Formulare
          </Typography>
          <ResponsiveListSurface variant="compactStandalone">
            <AutoFormsDataGrid autoForms={deletedDrivingSchoolAutoForms} />
          </ResponsiveListSurface>
        </Column>
      )}
      {drivingSchool?.id && <AddAutoFormDialog {...createDialogProps} drivingSchoolId={drivingSchool.id} />}
    </Column>
  );
}
